import React, { type CSSProperties, type ComponentProps } from 'react';
import styled, { css } from 'styled-components';
import clsx from 'clsx';
import {
  height,
  letterSpacing,
  space,
  fontSize,
  type AlignSelfProps,
  type DisplayProps,
  type FontSizeProps,
  type GridAreaProps,
  type HeightProps,
  type JustifyContentProps,
  type JustifySelfProps,
  type LetterSpacingProps,
  type SpaceProps
} from 'styled-system';

import {
  button,
  buttonReset,
  fontFamilySans,
  getThemeColor,
  transitionDefault
} from '../utils/styles';
import Link from './link';
import Icon, { type IconName } from './icon';
import mq from '../utils/mq';

interface ButtonVariantProps {
  primary?: boolean;
  fitContentWidth?: boolean;
  borderPrimaryColorPrimary?: boolean;
  borderColorPrimary?: boolean;
  whiteTransparent?: boolean;
  navigationCtaButton?: boolean;
  borderPurpleColorWhite?: boolean;
  borderGrey?: boolean;
  primaryTransparent?: boolean;
  greyTransparent?: boolean;
  transparentInverted?: boolean;
  transparent?: boolean;
  inDarkBlock?: boolean;
  whiteInDark?: boolean;
  white?: boolean;
  whitePurple?: boolean;
  bigPadding?: boolean;
  small?: boolean;
  invalid?: boolean;
  disabled?: boolean;
}

type ButtonStyleProps = SpaceProps &
  HeightProps &
  LetterSpacingProps &
  FontSizeProps &
  DisplayProps &
  AlignSelfProps &
  JustifySelfProps &
  GridAreaProps &
  JustifyContentProps;

type StyledButtonProps = ComponentProps<typeof Link> & ButtonStyleProps;

function getButtonDisplayValue(displayValue: StyledButtonProps['display']) {
  return typeof displayValue === 'string' ? displayValue : 'grid';
}

function getButtonHeightValue(heightValue: StyledButtonProps['height']) {
  if (typeof heightValue === 'number') {
    return `${heightValue}px`;
  }

  if (typeof heightValue === 'string') {
    return heightValue;
  }

  if (Array.isArray(heightValue)) {
    const firstValue = heightValue.find(
      (value): value is string | number =>
        typeof value === 'string' || typeof value === 'number'
    );

    if (typeof firstValue === 'number') {
      return `${firstValue}px`;
    }

    if (typeof firstValue === 'string') {
      return firstValue;
    }
  }

  return 'auto';
}

const StyledButton = styled(Link)<StyledButtonProps>`
  ${transitionDefault};
  ${fontFamilySans};
  ${buttonReset};
  ${button};
  ${height};
  ${space};
  ${letterSpacing};
  ${fontSize};
  letter-spacing: 0;
  display: ${(props) => getButtonDisplayValue(props.display)};
  grid-auto-flow: column;
  gap: 13px;
  border-radius: 30px;
  background-color: #813dff;
  transition: 0.16s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  transition-property: border, background-color, color, box-shadow, opacity,
    filter, transform;
  white-space: nowrap;
  overflow: hidden;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;

  ${mq.mobile(css`
    padding: 0 24px;
    height: 42px;
    font-size: 14px;
    line-height: 1;
  `)}

  &:hover {
    background-color: ${getThemeColor('black')};
    &:active {
      opacity: 0.8;
    }
  }

  &.button--in-dark-block {
    &:hover {
      background-color: #fff;
      color: ${getThemeColor('black')};
    }
  }

  &.button--white-in-dark {
    background-color: ${getThemeColor('white')};
    color: ${getThemeColor('black')};

    &:hover {
      background-color: #813dff;
      color: #fff;
    }
  }

  &.button--border-color-primary {
    background-color: transparent;
    border-color: #813dff;
    color: #fff;

    &:hover {
      background-color: #813dff;
    }
  }

  &.button--border-primary-color-primary {
    background-color: transparent;
    border-color: ${getThemeColor('primary')};
    color: ${getThemeColor('primary')};

    &:hover {
      background-color: ${getThemeColor('primary')};
      color: #fff;
    }
  }

  &.button--grey-transparent {
    background-color: transparent;
    color: ${getThemeColor('grey7')};
    padding: 0;

    &:hover,
    &:focus {
      background-color: transparent;
      color: ${getThemeColor('grey')};
    }
  }

  &.button--primary-transparent {
    background-color: transparent;
    color: ${getThemeColor('primary')};
    padding: 0;
    height: var(--button-transparent-height, auto);

    &:hover,
    &:focus {
      background-color: transparent;
      color: ${getThemeColor('vividPrimary')};
    }
  }

  &.button--border-purple-color-white {
    background-color: transparent;
    border-color: ${getThemeColor('purple')};
    color: #fff;

    &:hover {
      background-color: #fff;
      border-color: #fff;
      color: ${getThemeColor('purple')};
    }
  }

  &.button--border-grey {
    background-color: transparent;
    border: 1px ${getThemeColor('borderLight')} solid;
    color: ${getThemeColor('black')};

    &:hover {
      background-color: ${getThemeColor('black')};
      border-color: ${getThemeColor('black')};
      color: #fff;
    }
  }

  &.button--white-transparent {
    background-color: transparent;
    color: ${getThemeColor('black')};
    padding: 0;

    &:hover {
      background-color: transparent;
      color: ${getThemeColor('primary')};
    }
  }

  &.button--white {
    background-color: #fff;
    color: ${getThemeColor('black')};
  }

  &.button--white-purple {
    background-color: #fff;
    color: ${getThemeColor('black')};

    &:hover {
      background-color: ${getThemeColor('purple')};
      color: #fff;
    }
  }

  &.button--transparent {
    background-color: transparent;
    border-color: #474747;
    color: #fff;

    &:hover {
      background-color: #fff;
      border-color: #fff;
      color: ${getThemeColor('black')};
    }
  }

  &.button--transparent-inverted {
    background-color: transparent;
    border-color: ${getThemeColor('grey4')};
    color: ${getThemeColor('black')};

    &:hover {
      background-color: ${getThemeColor('black')};
      border-color: ${getThemeColor('black')};
      color: ${getThemeColor('white')};
    }
  }

  &.button--navigation-cta-button {
    height: 36px;
    padding: 0 16px;
    font-size: 14px;
    font-weight: 600;
    line-height: 140%;
    ${mq.mobile(css`
      height: 28px;
      padding: 0 8px;
      font-size: 12px;
    `)};
  }

  &.button--big-padding {
    padding: 0 72px;
  }

  &.button--small {
    height: 36px;
    padding: 0 14px;
  }

  &.button--invalid {
    border-color: ${getThemeColor('danger')};
  }

  &.button--disabled,
  &:disabled {
    pointer-events: none;
    opacity: 0.7;
  }

  &.button--fit-content-width {
    display: inline-grid;
    width: fit-content;
    ${mq.mobile(css` {
      width: 100%;
    `)};
  }
`;

type ButtonProps = StyledButtonProps &
  ButtonVariantProps & {
    chevronRight?: boolean;
    iconLeft?: IconName;
    iconRight?: IconName;
  };

function Button({
  children,
  chevronRight,
  iconLeft,
  iconRight,
  fitContentWidth,
  navigationCtaButton,
  borderPrimaryColorPrimary,
  borderPurpleColorWhite,
  borderColorPrimary,
  whiteTransparent,
  borderGrey,
  primaryTransparent,
  greyTransparent,
  transparentInverted,
  transparent,
  inDarkBlock,
  whiteInDark,
  white,
  whitePurple,
  bigPadding,
  small,
  invalid,
  disabled,
  className,
  style,
  height,
  ...rest
}: ButtonProps) {
  const buttonClassName = clsx(className, {
    'button--border-color-primary': borderColorPrimary,
    'button--border-primary-color-primary': borderPrimaryColorPrimary,
    'button--white-transparent': whiteTransparent,
    'button--border-purple-color-white': borderPurpleColorWhite,
    'button--border-grey': borderGrey,
    'button--primary-transparent': primaryTransparent,
    'button--grey-transparent': greyTransparent,
    'button--transparent-inverted': transparentInverted,
    'button--transparent': transparent,
    'button--in-dark-block': inDarkBlock,
    'button--white-in-dark': whiteInDark,
    'button--white': white,
    'button--white-purple': whitePurple,
    'button--big-padding': bigPadding,
    'button--small': small,
    'button--invalid': invalid,
    'button--disabled': disabled,
    'button--fit-content-width': fitContentWidth,
    'button--navigation-cta-button': navigationCtaButton
  });

  const buttonStyle = primaryTransparent
    ? ({
        ...style,
        ['--button-transparent-height' as const]: getButtonHeightValue(height)
      } as CSSProperties)
    : style;

  return (
    <StyledButton
      {...rest}
      className={buttonClassName}
      style={buttonStyle}
      height={height}
    >
      {iconLeft && (
        <Icon name={iconLeft} fillColor="white" strokeColor="white" />
      )}
      {children}
      {chevronRight && (
        <Icon name="chevronRight" fillColor="white" strokeColor="white" />
      )}
      {iconRight && (
        <Icon name={iconRight} fillColor="white" strokeColor="white" />
      )}
    </StyledButton>
  );
}

export default Button;
